import React, { useState, useEffect } from 'react';
import { formatDisplayDate, formatTimeDisplay } from '../../../../../lib/utils/dateTimeFormat';
import { fetchAllServices, fetchServiceAddons } from '../../../../../api/servicesAPI';
import { fetchAllVehicleTypes, fetchVehicleTypeWithPrices } from '../../../../../api/vehicleTypesAPI';
import { updateBooking, fetchBookingWithDetails } from '../../../../../api/bookingAPI';
import './EditBooking.css';

const EditBooking = ({ booking, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    booking_date: '',
    booking_time: '',
    service_id: '',
    vehicle_type_id: '',
    add_ons: [],
    total_price: 0,
    location_service: '',
    address: ''
  });

  const [services, setServices] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [serviceAddons, setServiceAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [basePrice, setBasePrice] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch services
        const { data: servicesData, error: servicesError } = await fetchAllServices();
        if (servicesError) throw servicesError;
        setServices(servicesData);

        // Fetch vehicle types
        const { data: vehicleTypesData, error: vehicleTypesError } = await fetchAllVehicleTypes();
        if (vehicleTypesError) throw vehicleTypesError;
        setVehicleTypes(vehicleTypesData);

        // Set initial form data
        if (booking) {
          setFormData({
            id: booking.id,
            first_name: booking.first_name,
            last_name: booking.last_name,
            email: booking.email,
            phone: booking.phone,
            booking_date: booking.booking_date,
            booking_time: booking.booking_time,
            service_id: booking.service_id,
            vehicle_type_id: booking.vehicle_type_id,
            add_ons: booking.booking_addons?.map(addon => addon.service_addons.id) || [],
            total_price: booking.total_price,
            location_service: booking.location_service || '',
            address: booking.address || ''
          });

          // Fetch addons for the selected service
          if (booking.service_id) {
            const { data: addonsData, error: addonsError } = await fetchServiceAddons(booking.service_id);
            if (addonsError) throw addonsError;
            setServiceAddons(addonsData);
          }

          // Fetch base price for the selected vehicle type and service
          if (booking.vehicle_type_id && booking.service_id) {
            const { data: vehicleData, error: vehicleError } = await fetchVehicleTypeWithPrices(booking.vehicle_type_id);
            if (vehicleError) throw vehicleError;

            const servicePrice = vehicleData.services_base_price.find(
              price => price.services.id === booking.service_id
            );
            if (servicePrice) {
              setBasePrice(servicePrice.price);
            }
          }
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [booking]);

  const calculateTotalPrice = async (serviceId, vehicleTypeId, selectedAddons, locationService) => {
    try {
      if (!serviceId || !vehicleTypeId) return 0;

      // Fetch base price
      const { data: vehicleData, error: vehicleError } = await fetchVehicleTypeWithPrices(vehicleTypeId);
      if (vehicleError) throw vehicleError;

      const servicePrice = vehicleData.services_base_price.find(
        price => price.services.id === serviceId
      );
      if (!servicePrice) return 0;

      // Calculate addons total
      const { data: addonsData, error: addonsError } = await fetchServiceAddons(serviceId);
      if (addonsError) throw addonsError;

      const addonsTotal = selectedAddons.reduce((total, addonId) => {
        const addon = addonsData.find(a => a.id === addonId);
        return total + (addon?.price || 0);
      }, 0);

      // Add $50 fee for at-home service
      const atHomeFee = locationService === 'atHome' ? 50 : 0;

      return servicePrice.price + addonsTotal + atHomeFee;
    } catch (error) {
      console.error('Error calculating total price:', error);
      return 0;
    }
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    const newFormData = {
      ...formData,
      [name]: value
    };

    // If service, vehicle type, or location changes, recalculate total price
    if (name === 'service_id' || name === 'vehicle_type_id' || name === 'location_service') {
      const total = await calculateTotalPrice(
        name === 'service_id' ? value : newFormData.service_id,
        name === 'vehicle_type_id' ? value : newFormData.vehicle_type_id,
        newFormData.add_ons, name === 'location_service' ? value : newFormData.location_service
      );
      newFormData.total_price = total;
    }

    setFormData(newFormData);

    // If service is changed, fetch its addons
    if (name === 'service_id') {
      const { data, error } = await fetchServiceAddons(value);
      if (error) {
        setError(error.message);
      } else {
        setServiceAddons(data);
        // Reset addons when service changes
        newFormData.add_ons = [];
        newFormData.total_price = await calculateTotalPrice(
          value,
          newFormData.vehicle_type_id,
          [],
          newFormData.location_service
        );
        setFormData(newFormData);
      }
    }
  };

  const handleAddOnChange = async (addOnId) => {
    const newAddons = formData.add_ons.includes(addOnId)
      ? formData.add_ons.filter(id => id !== addOnId)
      : [...formData.add_ons, addOnId];

    const total = await calculateTotalPrice(
      formData.service_id,
      formData.vehicle_type_id,
      newAddons,
      formData.location_service
    );

    setFormData(prev => ({
      ...prev,
      add_ons: newAddons,
      total_price: total
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const updatedBooking = {
        ...booking,
        ...formData,
        booking_addons: formData.add_ons.map(addonId => ({
          booking_id: booking.id,
          addon_id: addonId
        }))
      };


      const { data, error } = await updateBooking(booking.id, updatedBooking);

      if (error) {
        console.error('Failed to update booking:', error);
        setError(error.message);
        return;
      }

      // Fetch the complete booking data with add-on details using the new function
      const { data: completeBooking, error: fetchError } = await fetchBookingWithDetails(booking.id);

      if (fetchError) {
        console.error('Failed to fetch updated booking details:', fetchError);
        setError(fetchError.message);
        return;
      }

      // send email to customer
      try {
        await fetch(
          `${process.env.REACT_APP_SUPABASE_FUNCTION_URL}/send-booking-changes`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              firstName: completeBooking.profiles?.first_name || completeBooking.guest_profiles?.first_name,
              lastName: completeBooking.profiles?.last_name || completeBooking.guest_profiles?.last_name,
              email: completeBooking.profiles?.email || completeBooking.guest_profiles?.email,
              phone: completeBooking.profiles?.phone_number || completeBooking.guest_profiles?.phone_number,
              date: formatDisplayDate(completeBooking.booking_date),
              time: formatTimeDisplay(completeBooking.booking_time),
              vehicleType: completeBooking.vehicle_types?.title,
              serviceName: completeBooking.services?.name,
              addons: completeBooking.booking_addons?.map(addon => addon.service_addons?.name),
              totalPrice: completeBooking.total_price,
              bookingId: completeBooking.id,
              locationService: completeBooking.location_service,
              address: completeBooking.address
            }),
          }
        );
      } catch (emailError) {
        console.error('Error sending booking updates email:', emailError);
      }

      onClose();
    } catch (err) {
      console.error('Error in handleSubmit:', err);
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="edit-booking-overlay">
        <div className="edit-booking-modal">
          <div className="loading">Loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="edit-booking-overlay">
        <div className="edit-booking-modal">
          <div className="error-message">{error}</div>
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-booking-overlay" onClick={onClose}>
      <div className="edit-booking-modal" onClick={e => e.stopPropagation()}>
        <div className="edit-booking-header">
          <h2>Edit Booking</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="edit-booking-form">
          <div className="form-group">
            <label>Date</label>
            <input
              type="date"
              name="booking_date"
              value={formData.booking_date}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Time</label>
            <input
              type="time"
              name="booking_time"
              value={formData.booking_time}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Vehicle Type</label>
            <select
              name="vehicle_type_id"
              value={formData.vehicle_type_id}
              onChange={handleChange}
              required
            >
              <option value="">Select a vehicle type</option>
              {vehicleTypes.map(vehicle => (
                <option key={vehicle.id} value={vehicle.id}>
                  {vehicle.title}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Service</label>
            <select
              name="service_id"
              value={formData.service_id}
              onChange={handleChange}
              required
            >
              <option value="">Select a service</option>
              {services.map(service => (
                <option key={service.id} value={service.id}>
                  {service.name}
                </option>
              ))}
            </select>
          </div>

          {serviceAddons.length > 0 && (
            <div className="form-group">
              <label>Add-ons</label>
              <div className="add-ons-list">
                {serviceAddons.map(addon => (
                  <div key={addon.id} className="add-on-item">
                    <input
                      type="checkbox"
                      id={`addon-${addon.id}`}
                      checked={formData.add_ons.includes(addon.id)}
                      onChange={() => handleAddOnChange(addon.id)}
                    />
                    <label htmlFor={`addon-${addon.id}`}>
                      {addon.name} (${addon.price})
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="form-group">
            <label>Total Price</label>
            <div className="total-price">
              ${formData.total_price.toFixed(2)}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="location_service">Service Location</label>
            <select
              id="location_service"
              name="location_service"
              value={formData.location_service}
              onChange={handleChange}
              required
            >
              <option value="">Select Location</option>
              <option value="inStore">In Shop</option>
              <option value="atHome">At Home</option>
            </select>
          </div>

          {formData.location_service === 'atHome' && (
            <div className="form-group">
              <label htmlFor="address">Service Address</label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Enter service address"
                required
              />
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="save-btn">
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditBooking;
